import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, BadgeCheck, Check, ChevronLeft, ChevronRight, Flag, Star, X } from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/products/$id")({
  component: ProductDetail,
});

const gallery = [
  "https://images.unsplash.com/photo-1541167760496-1628856ab772?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1509042239860-f550ce710b93?q=80&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1461023058943-07fcbe16d735?q=80&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1494314671902-399b18174975?q=80&w=800&auto=format&fit=crop",
];

const specKeys = ["sku", "prep", "availability", "custom", "calories"] as const;

const stats = [
  { key: "orders", value: "847" as React.ReactNode },
  { key: "revenue", value: <Money value="23,716" /> },
  { key: "rating", value: "4.7 ★" },
  { key: "return", value: "82%" },
] as const;

const compliance = [
  { key: "image", status: "Pass" as const },
  { key: "pricing", status: "Pass" as const },
  { key: "health", status: "Fail" as const },
  { key: "classification", status: "Pass" as const },
];

const timeline = [
  { key: "flagged", time: "Jun 12, 2025 · 9:42 AM", tone: "rose" as const },
  { key: "submitted", time: "Jun 12, 2025 · 9:15 AM", tone: "primary" as const },
  { key: "approved", time: "Jan 15, 2024 · 2:30 PM", tone: "emerald" as const },
];

function ProductDetail() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [notes, setNotes] = useState("");

  return (
    <AdminShell
      title={t("pages.admin.productDetail.title")}
      subtitle={t("pages.admin.productDetail.subtitle")}
      actions={
        <Button asChild variant="outline" className="rounded-xl">
          <Link to="/admin/products">
            <ArrowLeft className="me-1.5 h-4 w-4 rtl:rotate-180" /> {t("adminProducts.detail.back")}
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
            <div className="relative aspect-[16/10] w-full bg-surface">
              <img src={gallery[active]} alt="Product" className="h-full w-full object-cover" />
              <div className="absolute start-4 top-4 flex gap-2">
                <Badge className="rounded-full border border-rose-500/20 bg-rose-500/10 text-rose-700 hover:bg-rose-500/10">
                  <Flag className="me-1 h-3 w-3" /> {t("adminProducts.detail.flagged")}
                </Badge>
                <Badge
                  variant="outline"
                  className="rounded-full border-border/70 bg-white/80 text-[11px] font-medium backdrop-blur"
                >
                  Beverages
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 p-3">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setActive(i)}
                  className={cn(
                    "relative aspect-[4/3] overflow-hidden rounded-lg border-2 transition",
                    active === i ? "border-[#5958F3]" : "border-transparent",
                  )}
                >
                  <img src={src} alt="thumb" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-2xl font-bold tracking-tight">{t("adminProducts.detail.name")}</h2>
                <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                  {t("adminProducts.detail.description")}
                </p>
              </div>
              <div className="text-end">
                <div className="text-2xl font-bold"><Riyal /> 28</div>
                <Badge className="mt-1 rounded-full border border-rose-500/20 bg-rose-500/10 text-rose-700 hover:bg-rose-500/10">
                  {t("adminProducts.detail.flagged")}
                </Badge>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border/60 pt-5 sm:grid-cols-3">
              {specKeys.map((key) => (
                <div key={key}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`adminProducts.detail.specs.${key}.label`)}
                  </div>
                  <div className="mt-0.5 text-sm font-medium">{t(`adminProducts.detail.specs.${key}.value`)}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl bg-surface p-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
                AL
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  {t("adminProducts.detail.merchantName")}
                  <BadgeCheck className="h-4 w-4 text-primary" />
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {t("adminProducts.detail.merchantMeta")}{" "}
                  <span className="inline-flex items-center gap-0.5">
                    4.8 <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("adminProducts.detail.productDetails")}</h3>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {stats.map((s) => (
                <div key={s.key} className="rounded-xl border border-border/60 bg-surface p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`adminProducts.detail.stats.${s.key}.label`)}
                  </div>
                  <div className="mt-1 text-xl font-bold">{s.value}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {t(`adminProducts.detail.stats.${s.key}.hint`)}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("adminProducts.detail.complianceTitle")}
            </h3>
            <ul className="mt-4 space-y-2">
              {compliance.map((c) => {
                const pass = c.status === "Pass";
                return (
                  <li
                    key={c.key}
                    className="flex items-center justify-between rounded-xl border border-border/60 bg-surface px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          "grid h-6 w-6 place-items-center rounded-full",
                          pass ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-600",
                        )}
                      >
                        {pass ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      </span>
                      <span className="text-sm font-medium">{t(`adminProducts.detail.compliance.${c.key}`)}</span>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[11px] font-medium",
                        pass
                          ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-700"
                          : "border-rose-500/20 bg-rose-500/10 text-rose-700",
                      )}
                    >
                      {t(`adminProducts.detail.compliance.${pass ? "pass" : "fail"}`)}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("adminProducts.detail.notesTitle")}</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("adminProducts.detail.notesPlaceholder")}
              className="mt-3 min-h-[110px] rounded-xl border-border/70 bg-surface"
            />
            <div className="mt-3 flex justify-end">
              <Button className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90">{t("adminProducts.detail.saveNote")}</Button>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("adminProducts.detail.submitted")}
                </div>
                <div className="mt-1 text-sm font-semibold">Jun 12, 2025</div>
                <div className="text-[11px] text-muted-foreground">{t("adminProducts.detail.submittedAgo")}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("adminProducts.detail.lastUpdated")}
                </div>
                <div className="mt-1 text-sm font-semibold">Jun 12, 2025</div>
                <div className="text-[11px] text-muted-foreground">{t("adminProducts.detail.byMerchant")}</div>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("adminProducts.detail.timelineTitle")}
            </h3>
            <ol className="mt-4 space-y-4">
              {timeline.map((item, i) => (
                <li key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
                        item.tone === "rose" && "bg-rose-500",
                        item.tone === "primary" && "bg-[#5958F3]",
                        item.tone === "emerald" && "bg-emerald-500",
                      )}
                    />
                    {i < timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
                  </div>
                  <div className="pb-2">
                    <div className="text-sm font-medium">{t(`adminProducts.detail.timeline.${item.key}`)}</div>
                    <div className="text-[11px] text-muted-foreground">{item.time}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
